<script lang="ts">
	import { locale } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import type { DocContent, Language } from '$lib/docs/types';
	import TableOfContents from '$lib/components/docs/TableOfContents.svelte';
	import PageNavigation from '$lib/components/docs/PageNavigation.svelte';
	import RelatedArticles from '$lib/components/docs/RelatedArticles.svelte';
	import {
		processMarkdownWithOS,
		extractTableOfContentsWithOS
	} from '$lib/utils/markdown-enhanced';
	import { initializeCopyButtons } from '$lib/utils/markdown';
	import { initializeEnhancedCodeBlocks } from '$lib/utils/enhanced-code-blocks';
	import '$lib/styles/markdown.css';
	import '$lib/styles/os-code-blocks.css';
	import '$lib/styles/enhanced-code-blocks.css';

	let { data } = $props();

	const slug = $derived(data.slug);
	const currentLocale = $derived(($locale as Language) || 'en');

	// Get content for current page
	const content = $derived(
		data.content || {
			id: slug,
			title: 'Page Not Found',
			description: 'The requested documentation page could not be found.',
			content: '# Page Not Found\n\nThe documentation page you are looking for does not exist.',
			lastUpdated: new Date().toISOString().split('T')[0],
			tags: [],
			tableOfContents: []
		}
	);

	// Process markdown content. Strip the leading "# Title" heading since the page
	// header already renders content.title as the H1 (avoids a duplicate H1).
	const bodyContent = $derived(content.content.replace(/^﻿?\s*#\s+[^\n]*\r?\n+/, ''));
	const processedContent = $derived(processMarkdownWithOS(bodyContent));
	const tableOfContents = $derived(extractTableOfContentsWithOS(content.content));

	// Generate meta info
	const lastUpdatedFormatted = $derived(
		new Date(content.lastUpdated).toLocaleDateString(currentLocale, {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		})
	);

	// Lightbox state — clicking a doc image opens it larger in a modal.
	let lightboxSrc = $state('');
	let lightboxAlt = $state('');
	let proseEl: HTMLElement | undefined;

	function openLightbox(src: string, alt: string) {
		lightboxSrc = src;
		lightboxAlt = alt;
	}
	function closeLightbox() {
		lightboxSrc = '';
		lightboxAlt = '';
	}

	function onProseClick(e: MouseEvent) {
		const target = e.target as HTMLElement;
		if (target?.tagName === 'IMG' && target.closest('.doc-page__prose')) {
			const img = target as HTMLImageElement;
			openLightbox(img.currentSrc || img.src, img.alt || '');
		}
	}
	function onKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape') closeLightbox();
	}

	// Initialize features after mount and on navigation
	onMount(() => {
		// Small delay to ensure DOM is ready
		const timeoutId = setTimeout(() => {
			initializeCopyButtons();
			initializeEnhancedCodeBlocks();
		}, 0);

		proseEl?.addEventListener('click', onProseClick);

		// Cleanup
		return () => {
			clearTimeout(timeoutId);
			proseEl?.removeEventListener('click', onProseClick);
		};
	});
</script>

<svelte:window onkeydown={onKeydown} />

<svelte:head>
	<title>{content.title} - Documentation - Heyweek</title>
	<meta name="description" content={content.description} />
	<meta name="keywords" content={content.tags.join(', ')} />

	<!-- Critical: Apply user preferences before any rendering -->
	<script>
		// This runs immediately, before the page renders
		(function () {
			// Get stored preferences or detect OS
			var os = localStorage.getItem('heyweek-selected-os');
			if (!os) {
				var p = (navigator.platform || '').toLowerCase();
				var u = (navigator.userAgent || '').toLowerCase();
				if (p.indexOf('mac') >= 0 || u.indexOf('mac') >= 0) os = 'macos';
				else if (p.indexOf('win') >= 0 || u.indexOf('win') >= 0) os = 'windows';
				else os = 'linux';
				// Store for next time
				try {
					localStorage.setItem('heyweek-selected-os', os);
				} catch (e) {}
			}

			var lang = localStorage.getItem('heyweek-selected-language') || 'bash';

			// Critical styles - inject immediately
			var style = document.createElement('style');
			style.id = 'code-block-init';
			style.textContent =
				'.os-code-content[data-os="' +
				os +
				'"] { display: block !important; }' +
				'.os-code-content:not([data-os="' +
				os +
				'"]) { display: none !important; }' +
				'.enhanced-code-content[data-os="' +
				os +
				'"][data-lang="' +
				lang +
				'"] { display: block !important; }' +
				'.enhanced-code-content:not([data-os="' +
				os +
				'"]) { display: none !important; }' +
				'.enhanced-code-content[data-os="' +
				os +
				'"]:not([data-lang="' +
				lang +
				'"]) { display: none !important; }' +
				'.enhanced-code-lang-tab[data-lang="' +
				lang +
				'"] { background: var(--bg-primary); color: var(--text-primary); box-shadow: 0 1px 3px rgba(0,0,0,0.1); }' +
				'.os-code-tab[data-os="' +
				os +
				'"] { background: var(--bg-primary); color: var(--text-primary); }';

			// Insert at the beginning of head for highest priority
			var firstStyle = document.head.querySelector('style');
			if (firstStyle) {
				document.head.insertBefore(style, firstStyle);
			} else {
				document.head.appendChild(style);
			}
		})();
	</script>
	<noscript>
		<style>
			/* Fallback: show first variant if JS is disabled */
			.enhanced-code-content:first-of-type {
				display: block !important;
			}
			.os-code-content:first-of-type {
				display: block !important;
			}
		</style>
	</noscript>

	<!-- Open Graph -->
	<meta property="og:title" content={`${content.title} - Heyweek Documentation`} />
	<meta property="og:description" content={content.description} />
	<meta property="og:type" content="article" />
	<meta property="og:url" content={`https://heyweek.com/docs/${slug}`} />

	<!-- Canonical (docs are served per-locale at one URL via cookie) -->
	<link rel="canonical" href={`https://heyweek.com/docs/${slug}`} />

	<!-- Article structured data — built with JSON.stringify so titles/descriptions
	     containing quotes or markup can't break the JSON or inject script. -->
	{@html `<script type="application/ld+json">${JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'Article',
		headline: content.title,
		description: content.description,
		dateModified: content.lastUpdated,
		author: { '@type': 'Organization', name: 'Heyweek' },
		publisher: { '@type': 'Organization', name: 'Heyweek' }
	}).replace(/</g, '\\u003c')}</script>`}
</svelte:head>

<div class="doc-page">
	<div class="doc-page__container">
		<article class="doc-page__content">
			<header class="doc-page__header">
				<h1 class="doc-page__title">{content.title}</h1>
				<p class="doc-page__description">{content.description}</p>

				<div class="doc-page__meta">
					<div class="doc-page__meta-item">
						<span class="doc-page__meta-label">Last updated:</span>
						<time class="doc-page__meta-value" datetime={content.lastUpdated}>
							{lastUpdatedFormatted}
						</time>
					</div>

					{#if content.tags.length > 0}
						<div class="doc-page__meta-item">
							<span class="doc-page__meta-label">Tags:</span>
							<div class="doc-page__tags">
								{#each content.tags as tag}
									<a href="/docs/tags/{encodeURIComponent(tag)}" class="doc-page__tag">
										#{tag}
									</a>
								{/each}
							</div>
						</div>
					{/if}
				</div>
			</header>

			<div class="doc-page__body">
				<div class="doc-page__prose" bind:this={proseEl}>
					{@html processedContent}
				</div>
			</div>

			<RelatedArticles currentSlug={slug} tags={content.tags} locale={currentLocale} limit={4} />

			<PageNavigation previousPage={content.previousPage} nextPage={content.nextPage} />
		</article>

		{#if tableOfContents.length > 0}
			<TableOfContents
				items={tableOfContents.map((item) => ({
					title: item.text,
					anchor: item.anchor,
					level: item.level
				}))}
			/>
		{/if}
	</div>
</div>

{#if lightboxSrc}
	<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
	<div class="doc-lightbox" onclick={closeLightbox}>
		<button class="doc-lightbox__close" aria-label="Close" onclick={closeLightbox}>&times;</button>
		<img class="doc-lightbox__img" src={lightboxSrc} alt={lightboxAlt} />
	</div>
{/if}

<style>
	/* Image lightbox */
	.doc-lightbox {
		position: fixed;
		inset: 0;
		z-index: 1000;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 2rem;
		background: rgba(0, 0, 0, 0.8);
		cursor: zoom-out;
		backdrop-filter: blur(4px);
	}

	.doc-lightbox__img {
		max-width: 95vw;
		max-height: 90vh;
		border-radius: 8px;
		box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5);
	}

	.doc-lightbox__close {
		position: absolute;
		top: 1rem;
		right: 1.25rem;
		width: 2.5rem;
		height: 2.5rem;
		border: none;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.15);
		color: #fff;
		font-size: 1.75rem;
		line-height: 1;
		cursor: pointer;
	}

	.doc-lightbox__close:hover {
		background: rgba(255, 255, 255, 0.3);
	}

	.doc-page {
		flex: 1;
		min-width: 0;
	}

	.doc-page__container {
		display: flex;
		gap: 3rem;
		max-width: 1400px;
		margin: 0 auto;
		position: relative;
	}

	.doc-page__content {
		flex: 1;
		min-width: 0;
		max-width: 800px;
	}

	.doc-page__header {
		margin-bottom: 2rem;
		padding-bottom: 1.5rem;
		border-bottom: 1px solid var(--border-light);
	}

	.doc-page__title {
		font-size: 2rem;
		font-weight: 600;
		color: var(--text-primary);
		margin-bottom: 1rem;
		line-height: 1.3;
		letter-spacing: -0.01em;
	}

	.doc-page__description {
		font-size: 1rem;
		color: var(--text-secondary);
		margin-bottom: 1.5rem;
		line-height: 1.5;
	}

	.doc-page__meta {
		display: flex;
		flex-wrap: wrap;
		gap: 2rem;
	}

	.doc-page__meta-item {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.doc-page__meta-label {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		font-weight: 500;
	}

	.doc-page__meta-value {
		font-size: 0.75rem;
		color: var(--text-secondary);
	}

	.doc-page__tags {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
	}

	.doc-page__tag {
		display: inline-block;
		padding: 0.25rem 0.5rem;
		background: var(--bg-secondary);
		color: var(--text-secondary);
		font-size: 0.75rem;
		border-radius: 4px;
		border: 1px solid var(--border-light);
		text-decoration: none;
		transition: all 0.2s ease;
	}

	.doc-page__tag:hover {
		background: var(--bg-tertiary);
		border-color: var(--brand-primary);
		color: var(--brand-primary);
		transform: translateY(-1px);
	}

	.doc-page__body {
		margin-bottom: 2rem;
	}

	/* Prose styling */
	.doc-page__prose {
		color: var(--text-primary);
		line-height: 1.7;
	}

	.doc-page__prose :global(h1) {
		font-size: 1.75rem;
		font-weight: 600;
		margin: 2.5rem 0 1rem;
		color: var(--text-primary);
		line-height: 1.3;
		letter-spacing: -0.01em;
	}

	.doc-page__prose :global(h2) {
		font-size: 1.375rem;
		font-weight: 600;
		margin: 2rem 0 0.75rem;
		color: var(--text-primary);
		line-height: 1.4;
		letter-spacing: -0.005em;
	}

	.doc-page__prose :global(h3) {
		font-size: 1.125rem;
		font-weight: 600;
		margin: 1.75rem 0 0.5rem;
		color: var(--text-primary);
	}

	.doc-page__prose :global(h4) {
		font-size: 1rem;
		font-weight: 600;
		margin: 1.5rem 0 0.5rem;
		color: var(--text-primary);
	}

	.doc-page__prose :global(p) {
		margin: 1rem 0;
		color: var(--text-secondary);
		font-size: 0.875rem;
	}

	.doc-page__prose :global(ul),
	.doc-page__prose :global(ol) {
		margin: 1rem 0;
		padding-left: 1.5rem;
		color: var(--text-secondary);
	}

	.doc-page__prose :global(li) {
		margin: 0.5rem 0;
		font-size: 0.875rem;
	}

	/* Plain quotes only — callouts (.docs-callout) are styled by markdown.css */
	.doc-page__prose :global(blockquote.docs-quote) {
		margin: 1.5rem 0;
		padding: 0.75rem 1rem;
		background: var(--bg-secondary);
		border-left: 3px solid var(--brand-primary);
		border-radius: 0 4px 4px 0;
		color: var(--text-secondary);
		font-style: normal;
		font-size: 0.875rem;
	}

	.doc-page__prose :global(:not(pre) > code) {
		padding: 0.125rem 0.25rem;
		background: var(--bg-secondary);
		color: var(--text-primary);
		font-size: 0.8125rem;
		border-radius: 3px;
		font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
	}

	/* Legacy pre styles - not inside enhanced code blocks */
	.doc-page__prose :global(pre) {
		margin: 0;
		padding: 1rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 6px;
		overflow-x: auto;
		font-size: 0.8125rem;
	}

	/* Override for enhanced code blocks */
	.doc-page__prose :global(.enhanced-code-block pre),
	.doc-page__prose :global(.os-code-block pre) {
		background: transparent;
		border: none;
		padding: 0;
		margin: 0;
	}

	.doc-page__prose :global(pre code) {
		background: none;
		padding: 0;
		font-size: inherit;
	}

	.doc-page__prose :global(a) {
		color: var(--brand-primary);
		text-decoration: none;
		border-bottom: 1px solid transparent;
		transition: all 0.15s ease;
	}

	.doc-page__prose :global(a:hover) {
		border-bottom-color: var(--brand-primary);
	}

	.doc-page__prose :global(strong) {
		font-weight: 600;
		color: rgba(0, 0, 0, 0.9);
	}

	.doc-page__prose :global(em) {
		font-style: italic;
	}

	.doc-page__prose :global(hr) {
		margin: 3rem 0;
		border: none;
		border-top: 1px solid rgba(0, 0, 0, 0.06);
	}

	.doc-page__prose :global(table) {
		width: 100%;
		margin: 2rem 0;
		border-collapse: collapse;
		background: #fff;
		border: 1px solid rgba(0, 0, 0, 0.08);
		border-radius: 12px;
		overflow: hidden;
	}

	.doc-page__prose :global(th),
	.doc-page__prose :global(td) {
		padding: 1rem 1.25rem;
		text-align: left;
		border-bottom: 1px solid rgba(0, 0, 0, 0.06);
	}

	.doc-page__prose :global(th) {
		background: rgba(0, 0, 0, 0.03);
		font-weight: 500;
		color: rgba(0, 0, 0, 0.8);
		text-transform: uppercase;
		font-size: 0.8125rem;
		letter-spacing: 0.05em;
	}

	.doc-page__prose :global(td) {
		color: rgba(0, 0, 0, 0.65);
		font-size: 1rem;
	}

	.doc-page__prose :global(img) {
		max-width: 100%;
		height: auto;
		margin: 2rem 0;
		border-radius: 12px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		cursor: zoom-in;
	}

	/* Responsive adjustments */
	@media (max-width: 1200px) {
		.doc-page__container {
			flex-direction: column;
		}
	}

	/* Dark mode adjustments */
	:global([data-theme='dark']) .doc-page__header {
		border-bottom-color: rgba(255, 255, 255, 0.08);
	}

	:global([data-theme='dark']) .doc-page__title {
		color: rgba(255, 255, 255, 0.95);
	}

	:global([data-theme='dark']) .doc-page__description {
		color: rgba(255, 255, 255, 0.6);
	}

	:global([data-theme='dark']) .doc-page__meta-label {
		color: rgba(255, 255, 255, 0.4);
	}

	:global([data-theme='dark']) .doc-page__meta-value {
		color: rgba(255, 255, 255, 0.6);
	}

	:global([data-theme='dark']) .doc-page__tag {
		background: rgba(255, 255, 255, 0.06);
		color: rgba(255, 255, 255, 0.6);
	}

	:global([data-theme='dark']) .doc-page__prose {
		color: rgba(255, 255, 255, 0.8);
	}

	:global([data-theme='dark']) .doc-page__prose :global(h1),
	:global([data-theme='dark']) .doc-page__prose :global(h2),
	:global([data-theme='dark']) .doc-page__prose :global(h3),
	:global([data-theme='dark']) .doc-page__prose :global(h4) {
		color: rgba(255, 255, 255, 0.95);
	}

	:global([data-theme='dark']) .doc-page__prose :global(p),
	:global([data-theme='dark']) .doc-page__prose :global(ul),
	:global([data-theme='dark']) .doc-page__prose :global(ol),
	:global([data-theme='dark']) .doc-page__prose :global(li) {
		color: rgba(255, 255, 255, 0.7);
	}

	:global([data-theme='dark']) .doc-page__prose :global(strong) {
		color: rgba(255, 255, 255, 0.95);
	}

	:global([data-theme='dark']) .doc-page__prose :global(blockquote.docs-quote) {
		background: rgba(255, 94, 126, 0.08);
		border-left-color: rgba(255, 94, 126, 0.6);
		color: rgba(255, 255, 255, 0.7);
	}

	:global([data-theme='dark']) .doc-page__prose :global(:not(pre) > code) {
		background: rgba(255, 255, 255, 0.08);
		color: rgba(255, 255, 255, 0.85);
	}

	:global([data-theme='dark']) .doc-page__prose :global(pre) {
		background: rgba(0, 0, 0, 0.3);
		border-color: rgba(255, 255, 255, 0.1);
	}

	/* Override for enhanced code blocks in dark mode */
	:global([data-theme='dark']) .doc-page__prose :global(.enhanced-code-block pre),
	:global([data-theme='dark']) .doc-page__prose :global(.os-code-block pre) {
		background: transparent;
		border: none;
	}

	:global([data-theme='dark']) .doc-page__prose :global(a) {
		color: #ff7a99;
		border-bottom-color: rgba(255, 122, 153, 0.3);
	}

	:global([data-theme='dark']) .doc-page__prose :global(a:hover) {
		color: #ff5e7e;
		border-bottom-color: #ff5e7e;
	}

	:global([data-theme='dark']) .doc-page__prose :global(hr) {
		border-top-color: rgba(255, 255, 255, 0.08);
	}

	:global([data-theme='dark']) .doc-page__prose :global(table) {
		background: #1a1a1a;
		border-color: rgba(255, 255, 255, 0.1);
	}

	:global([data-theme='dark']) .doc-page__prose :global(th) {
		background: rgba(255, 255, 255, 0.05);
		color: rgba(255, 255, 255, 0.8);
	}

	:global([data-theme='dark']) .doc-page__prose :global(th),
	:global([data-theme='dark']) .doc-page__prose :global(td) {
		border-bottom-color: rgba(255, 255, 255, 0.08);
	}

	:global([data-theme='dark']) .doc-page__prose :global(td) {
		color: rgba(255, 255, 255, 0.65);
	}

	@media (max-width: 768px) {
		.doc-page__container {
			gap: 1rem;
		}

		.doc-page__header {
			margin-bottom: 2rem;
			padding-bottom: 1.5rem;
		}

		.doc-page__title {
			font-size: 2rem;
		}

		.doc-page__description {
			font-size: 1rem;
		}

		.doc-page__meta {
			flex-direction: column;
			gap: 1rem;
		}

		.doc-page__prose :global(h1) {
			font-size: 1.875rem;
		}

		.doc-page__prose :global(h2) {
			font-size: 1.5rem;
		}

		.doc-page__prose :global(h3) {
			font-size: 1.25rem;
		}
	}
</style>
